import type { ReactNode } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import SkipLink, { MAIN_ID } from '~/components/SkipLink'
import { Button } from '~/components/ui/button'
import { tourAnchor } from '~/helpers/tour/anchors'

type IProps = {
  /** Où mène la sortie, et son libellé. */
  fallbackTo: string
  fallbackLabel: string
  headerContent?: ReactNode
  headerRight?: ReactNode
  children: ReactNode
}

/**
 * Le cadre du lecteur (bandeau de sortie + corps), sans rien de SCORM : le
 * lecteur, la prévisualisation et leurs démonstrations du centre d'aide (#105)
 * l'habillent chacun de leur en-tête et de leur contenu.
 */
function PlayerFrame({ fallbackTo, fallbackLabel, headerContent, headerRight, children }: IProps) {
  const navigate = useNavigate()

  return (
    /*
      Le cadre suit le THÈME, il ne l'impose plus.

      Il était écrit en dur — `bg-black`, `text-white`, `border-neutral-800` —
      et la bascule clair/sombre du menu n'avait donc aucun effet ici : le
      lecteur restait noir quel que soit le choix de l'apprenant, alors que
      c'est lui qui décide. Des couleurs en dur excluaient au passage la marque
      de l'organisation (#31) de l'écran où il passe le plus de temps.
    */
    <div className="flex h-dvh flex-col bg-background text-foreground">
      <SkipLink />
      <header className="flex items-center justify-between gap-3 border-b border-border bg-background/80 px-3 py-2.5 backdrop-blur sm:px-6 sm:py-3">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          {/*
            La sortie est un BOUTON visible, plus un élément enfoui dans le menu
            déroulant : personne ne cherche « revenir à mes formations » derrière
            son propre nom, et l'apprenant se retrouvait enfermé dans le module.
            Le cadre connaît déjà où retourner — `fallbackTo` servait jusqu'ici
            aux seuls écrans de fin et d'indisponibilité.
          */}
          <Button variant="ghost" size="sm" className="shrink-0" onClick={() => navigate(fallbackTo)} {...tourAnchor('player.exit')}>
            <ArrowLeft className="size-4 sm:mr-2" />
            {/* Masqué à l'œil sur mobile, jamais au lecteur d'écran : c'est la seule sortie (RGAA 11.9). */}
            <span className="sr-only sm:not-sr-only">{fallbackLabel}</span>
          </Button>
          <span aria-hidden="true" className="hidden h-5 w-px shrink-0 bg-border sm:block" />
          <div className="flex min-w-0 items-center gap-3" {...tourAnchor('player.header')}>{headerContent}</div>
        </div>
        {headerRight}
      </header>
      <main id={MAIN_ID} tabIndex={-1} className="flex-1 focus:outline-none" {...tourAnchor('player.content')}>{children}</main>
    </div>
  )
}

export default PlayerFrame
